import { useCallback, useEffect, useState } from 'react'
import chatLog from '../global/chat-log'

const KEY = 'rtc-unread-messages'

const readCache = () => {
  const storageData = localStorage.getItem(KEY)
  if (storageData) {
    try {
      return JSON.parse(storageData)
    } catch {
      return []
    }
  }
  return []
}

const saveToCache = ({ data }) => {
  chatLog(`[Cache] Saving list to unread message cache: ${data}`)
  localStorage.setItem(KEY, JSON.stringify(data))
}

const removeCache = () => {
  localStorage.removeItem(KEY)
}

export const useLoadUnreadMessagesFromCache = ({ connection, dispatch }) => {
  const [hasLoaded, setHasLoaded] = useState(false)

  const loadFn = useCallback(() => {
    const unreadMessages = readCache()
    if (unreadMessages) {
      chatLog(`[Cache] Loading unread messages from cache: ${unreadMessages}`)
      dispatch({ type: 'LOAD_UNREAD_MESSAGES_FROM_CACHE', data: unreadMessages })
    }
  }, [dispatch])

  const storageSync = useCallback(
    (storageEvent) => {
      if (storageEvent?.key === KEY) {
        loadFn()
      }
    },
    [loadFn]
  )

  useEffect(() => {
    if (connection && !hasLoaded) {
      loadFn()

      // Adding listener in case the user has multiple tabs up.
      // If the localStorage key changes on another tab, update.
      window.addEventListener('storage', storageSync)
      setHasLoaded(true)
    }

    return () => {
      if (hasLoaded) {
        window.removeEventListener('storage', storageSync)
      }
    }
  }, [connection, hasLoaded, loadFn, storageSync])
}

export const useSaveUnreadMessageToCache = () => {
  const saveFn = useCallback(({ jid }) => {
    const data = readCache()
    if (!data.includes(jid)) {
      chatLog(`[Cache] Saving ${jid} to unread messages cache]`)
      saveToCache({ data: [...data, jid] })
    }
  }, [])

  return [saveFn]
}

export const useClearUnreadMessageFromCache = () => {
  const clearFn = useCallback(({ jid }) => {
    const data = readCache()
    if (data.includes(jid)) {
      chatLog(`[Cache] Clearing ${jid} from unread messages cache]`)
      const newData = data.filter((i) => i !== jid)
      saveToCache({ data: [...newData] })
    }
  }, [])

  return [clearFn]
}

export const useClearAllUnreadMessagesFromCache = () => {
  const removeFn = useCallback(() => {
    removeCache()
  }, [])
  return [removeFn]
}
